import React, { useCallback, useEffect, useRef, useState } from "react";
import { Mail, Download, RefreshCw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatVisitDate } from "@/lib/utils";
import { buildMailingCsv } from "@/lib/mailing-csv";
import { useAuth } from "@/context/AuthContext";

const PAGE_SIZE = 25;
const FILTERS = [
  { key: "all", label: "All" },
  { key: "no_email", label: "No email" },
  { key: "senior_email", label: "Senior with email" },
];
const muted = { color: "var(--fb-text-muted)" };

export default function SharingTreeMailingReport() {
  const { user } = useAuth() || {};
  const canSpend = user?.role !== "viewer";
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [group, setGroup] = useState("all");
  const [page, setPage] = useState(0);
  const [exportError, setExportError] = useState("");
  const requestRef = useRef(0);
  const mountedRef = useRef(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [checks, setChecks] = useState({});
  const [panel, setPanel] = useState(null); // {state, config, error}
  const [requested, setRequested] = useState("");
  const [recheckVerified, setRecheckVerified] = useState(false);
  const [batch, setBatch] = useState(null);
  const [tick, setTick] = useState(0);
  const batchRef = useRef(0);
  const cancelRef = useRef(false);
  const exportingRef = useRef(false);
  const [rowState, setRowState] = useState({}); // household_id -> {saving, error}

  const keepOriginal = (id) => setChecks((prev) => (prev[id] ? { ...prev, [id]: { ...prev[id], kept: true } } : prev));
  const acceptSuggestion = async (r) => {
    const c = validCheck(r);
    const a = c?.address;
    if (!c || c.kept || c.status !== "needs_review" || !c.suggestion_confirmed || !c.verification_token || !a) return;
    if (rowState[r.household_id]?.saving) return;
    setRowState((p) => ({ ...p, [r.household_id]: { saving: true, error: "" } }));
    try {
      const res = await api.post(`/clients/${r.household_id}/google-address/accept`, {
        address_fingerprint: r.address_fingerprint,
        address: { street: a.street, apt: a.apt || "", city: a.city, state: a.state, zip: a.zip },
        verification_token: c.verification_token,
      });
      if (!mountedRef.current) return;
      const d = res.data || {};
      setData((prev) => prev && { ...prev, rows: (prev.rows || []).map((x) => x.household_id === r.household_id ? {
        ...x, full_address: d.address, address_fingerprint: d.address_fingerprint,
        google_address_verified: !!d.google_address_verified, google_address_verification: d.google_address_verification,
      } : x) });
      setChecks((prev) => ({ ...prev, [r.household_id]: {
        household_id: r.household_id, address_fingerprint: d.address_fingerprint, status: "checked",
        expires_at: c.expires_at, message: "Address updated to the Google-confirmed suggestion.",
      } }));
      setRowState((p) => ({ ...p, [r.household_id]: { saving: false, error: "" } }));
    } catch (err) {
      if (!mountedRef.current) return;
      const msg = err?.response?.status === 409
        ? "This address or its Google check changed or expired. Refresh the report and check again."
        : formatApiError(err);
      setRowState((p) => ({ ...p, [r.household_id]: { saving: false, error: msg } }));
    }
  };

  const resetChecks = () => {
    batchRef.current += 1;
    cancelRef.current = true;
    setChecks({});
    setPanel(null);
    setBatch(null);
    setStatusFilter("all");
    setRecheckVerified(false);
  };

  const load = useCallback(() => {
    const id = ++requestRef.current;
    setStatus("loading");
    setError("");
    setExportError("");
    resetChecks();
    api
      .get("/reports/sharing-tree-mailing")
      .then((res) => {
        if (!mountedRef.current || id !== requestRef.current) return;
        setData(res.data);
        setPage(0);
        setStatus("ready");
      })
      .catch((err) => {
        if (!mountedRef.current || id !== requestRef.current) return;
        setError(formatApiError(err));
        setStatus("error");
      });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
      requestRef.current += 1;
      batchRef.current += 1;
      cancelRef.current = true;
    };
  }, [load]);

  const allRows = data?.rows || [];
  const groupRows = group === "all" ? allRows : allRows.filter((r) => r.group === group);
  const validCheck = (r) => {
    const c = checks[r.household_id];
    if (!c || !r.address_fingerprint || c.address_fingerprint !== r.address_fingerprint) return null;
    const exp = Date.parse(c.expires_at || "");
    if (Number.isNaN(exp) || exp <= Date.now()) return null;
    return c;
  };
  const isChecked = (r) => validCheck(r)?.status === "checked";
  const rows = statusFilter === "checked" ? groupRows.filter(isChecked) : groupRows;
  const tally = { checked: 0, needs_review: 0, not_checked: 0 };
  groupRows.forEach((r) => {
    const c = validCheck(r);
    const key = c && tally[c.status] !== undefined ? c.status : "not_checked";
    tally[key] += 1;
  });
  const savedVerified = (r) => !!(r.google_address_verified && r.address_fingerprint &&
    r.google_address_verification?.address_fingerprint === r.address_fingerprint);
  const sessionCandidates = groupRows.filter((r) => {
    const c = validCheck(r);
    return r.address_fingerprint && !(c && (c.status === "checked" || c.status === "needs_review"));
  });
  const savedMatches = sessionCandidates.filter(savedVerified).length;
  const candidates = recheckVerified ? sessionCandidates : sessionCandidates.filter((r) => !savedVerified(r));
  const hasChecks = Object.keys(checks).length > 0;

  // Keep review results tied to other staff's current address edits without
  // making any paid calls. Ordinary report loads retain the old behavior.
  useEffect(() => {
    if (!hasChecks) return undefined;
    let stopped = false;
    let pending = false;
    const generation = requestRef.current;
    const refreshOriginals = async () => {
      if (pending || document.visibilityState === "hidden") return;
      pending = true;
      try {
        const res = await api.get("/reports/sharing-tree-mailing");
        if (!stopped && mountedRef.current && requestRef.current === generation && Array.isArray(res.data?.rows)) {
          setData(res.data);
          const current = new Map(res.data.rows.map((r) => [r.household_id, r.address_fingerprint]));
          setChecks((prev) => Object.fromEntries(Object.entries(prev).filter(
            ([id, c]) => current.get(id) === c.address_fingerprint && Date.parse(c.expires_at || "") > Date.now())));
        }
      } catch {
        // A reviewed CSV must independently refresh successfully before export.
      } finally {
        pending = false;
      }
    };
    const timer = setInterval(refreshOriginals, 60000);
    window.addEventListener("focus", refreshOriginals);
    return () => { stopped = true; clearInterval(timer); window.removeEventListener("focus", refreshOriginals); };
  }, [hasChecks]);

  // Delete expired content, not just hide it. Tab timers may be throttled;
  // validCheck also checks the current clock at download/click time.
  useEffect(() => {
    const times = Object.values(checks).map((c) => Date.parse(c.expires_at || "")).filter((t) => !Number.isNaN(t) && t > Date.now());
    if (!times.length) return undefined;
    const id = setTimeout(() => {
      setChecks((prev) => Object.fromEntries(Object.entries(prev).filter(
        ([, c]) => Date.parse(c.expires_at || "") > Date.now())));
      setTick((t) => t + 1);
    }, Math.max(0, Math.min(...times) - Date.now()) + 5);
    return () => clearTimeout(id);
  }, [checks, tick]);

  const openPanel = () => {
    setPanel({ state: "loading" });
    const id = ++batchRef.current;
    api.get("/address-validation/config")
      .then((res) => {
        if (!mountedRef.current || id !== batchRef.current) return;
        setRequested("");
        setPanel({ state: "ready", config: res.data });
      })
      .catch((err) => {
        if (!mountedRef.current || id !== batchRef.current) return;
        setPanel({ state: "error", error: formatApiError(err) });
      });
  };

  const cfg = panel?.config;
  const maxAllowed = cfg ? Math.max(0, Math.min(100, cfg.max_run || 100, cfg.daily_remaining ?? 0, candidates.length)) : 0;
  const requestedNumber = requested === "" ? maxAllowed : Number(requested);
  const effective = Number.isInteger(requestedNumber) && requestedNumber > 0
    ? Math.min(maxAllowed, requestedNumber) : 0;

  const runBatch = async () => {
    if (batch?.running || !cfg || effective < 1 || !cfg.google_active) return;
    const runId = ++batchRef.current;
    cancelRef.current = false;
    const targets = candidates.slice(0, effective);
    const size = Math.min(10, cfg.batch_size || 10);
    let done = 0;
    let skipped = 0;
    setBatch({ running: true, done: 0, total: targets.length, failed: 0, error: "", cancelled: false });
    for (let i = 0; i < targets.length; i += size) {
      const chunk = targets.slice(i, i + size).map((r) => ({ household_id: r.household_id, address_fingerprint: r.address_fingerprint }));
      try {
        const res = await api.post("/reports/sharing-tree-mailing/check-addresses", {
          items: chunk, consent_google: true, recheck_verified: recheckVerified,
        });
        if (!mountedRef.current || runId !== batchRef.current) return;
        const results = res.data?.results;
        if (!Array.isArray(results) || results.length !== chunk.length) throw new Error("Incomplete address-check response. Unreturned addresses remain not checked.");
        setData((prev) => prev && { ...prev, rows: (prev.rows || []).map((r) => {
          const checked = results.find((x) => x.household_id === r.household_id &&
            x.address_fingerprint === r.address_fingerprint &&
            x.google_address_verified);
          return checked ? { ...r, google_address_verified: true,
            google_address_verification: checked.google_address_verification } : r;
        }) });
        setChecks((prev) => {
          const next = { ...prev };
          results.forEach((x) => {
            if (chunk.some((c) => c.household_id === x.household_id && c.address_fingerprint === x.address_fingerprint)) {
              const expiry = Date.parse(x.expires_at || "");
              if (x.reason !== "already_verified" && !Number.isNaN(expiry)) next[x.household_id] = {
                ...x, expires_at: new Date(Math.min(expiry, Date.now() + 900000)).toISOString(),
              };
            }
          });
          return next;
        });
        if (typeof res.data?.remaining === "number") {
          setPanel((p) => (p?.config ? { ...p, config: { ...p.config, daily_remaining: res.data.remaining } } : p));
        }
        done += chunk.length;
        skipped += results.filter((x) => x.reason === "already_verified").length;
      } catch (err) {
        if (!mountedRef.current || runId !== batchRef.current) return;
        setBatch({ running: false, done, skipped, total: targets.length, failed: targets.length - done, error: formatApiError(err), cancelled: false });
        return;
      }
      if (cancelRef.current) {
        setBatch({ running: false, done, skipped, total: targets.length, failed: 0, error: "", cancelled: true });
        return;
      }
      setBatch({ running: true, done, skipped, total: targets.length, failed: 0, error: "", cancelled: false });
    }
    setBatch({ running: false, done, skipped, total: targets.length, failed: 0, error: "", cancelled: false });
  };
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const visible = rows.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
  const loading = status === "loading";
  const counts = data?.counts || {};
  const period = data?.period;

  const download = async (format = "full") => {
    if (exportingRef.current) return;
    exportingRef.current = true;
    setExportError("");
    let url;
    try {
      let exportRows = groupRows;
      // No provider calls: re-read the original report before exporting review
      // results so another staff member's edit cannot export an old check.
      if (Object.keys(checks).length) {
        const res = await api.get("/reports/sharing-tree-mailing");
        if (!mountedRef.current) return;
        setData(res.data);
        const currentRows = res.data?.rows;
        if (!Array.isArray(currentRows)) throw new Error("Could not refresh addresses before export");
        exportRows = group === "all" ? currentRows : currentRows.filter((r) => r.group === group);
      }
      if (statusFilter === "checked") exportRows = exportRows.filter(isChecked);
      if (!exportRows.length) throw new Error("No current, unexpired addresses match this export");
      const currentChecks = {};
      exportRows.forEach((r) => { const c = validCheck(r); if (c) currentChecks[r.household_id] = c; });
      const csv = format === "avery"
        ? buildMailingCsv(exportRows, { format: "avery" })
        : Object.keys(currentChecks).length
        ? buildMailingCsv(exportRows, { checks: currentChecks }) : buildMailingCsv(exportRows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sharing-tree-mailing-${group}${statusFilter === "checked" ? "-checked" : ""}${format === "avery" ? "-avery" : ""}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      const u = url;
      setTimeout(() => window.URL.revokeObjectURL(u), 1000);
    } catch (err) {
      if (url) window.URL.revokeObjectURL(url);
      setExportError(`CSV download failed: ${err?.message || "unknown error"}. Try again.`);
    } finally {
      exportingRef.current = false;
    }
  };

  const setFilter = (k) => {
    setGroup(k);
    setPage(0);
    if (statusFilter === "checked") setStatusFilter("all");
    setExportError("");
  };

  const setStatus2 = (k) => { setStatusFilter(k); setPage(0); setExportError(""); };
  const running = !!batch?.running;

  return (
    <section className="fb-card p-4 sm:p-6" data-testid="sharing-tree-mailing-report" aria-labelledby="stm-heading">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
            <Mail className="w-5 h-5" aria-hidden="true" />
          </div>
          <div>
            <h2 id="stm-heading" className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Sharing Tree Mailing Report</h2>
            {period && (
              <p className="text-sm" style={muted} data-testid="stm-period">
                Visits from {period.start_date} to {period.end_date} ({period.timezone})
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={load} disabled={loading} data-testid="stm-refresh"
            className="px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
            style={{ border: "1px solid var(--fb-border)", color: "var(--fb-text-muted)" }}>
            <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
            {loading ? "Refreshing..." : "Refresh"}
          </button>
          <button type="button" onClick={() => download()} disabled={loading || status === "error" || rows.length === 0} data-testid="stm-download"
            className="px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: "var(--fb-primary)", color: "#fff" }}>
            <Download className="w-3.5 h-3.5" aria-hidden="true" />
            {statusFilter === "checked" ? "Download checked CSV" : "Download CSV"} ({rows.length})
          </button>
          <button type="button" onClick={() => download("avery")}
            disabled={loading || status === "error" || rows.length === 0} data-testid="stm-download-avery"
            title="First Name, Last Name, Street Address, City, State, ZIP"
            className="px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
            style={{ border: "1px solid var(--fb-primary)", color: "var(--fb-primary)" }}>
            <Download className="w-3.5 h-3.5" aria-hidden="true" />
            {statusFilter === "checked" ? "Export checked Avery CSV" : "Export Avery CSV"} ({rows.length})
          </button>
        </div>
      </div>

      <div className="text-sm mb-4" style={muted}>
        <p>A household is listed when all of these are true:</p>
        <ul className="list-disc pl-5 mt-1 space-y-0.5">
          <li>It visited within the last 12 months.</li>
          <li>It has a complete address on file.</li>
          <li>It has no email, or its primary household member is 65 or older and has a valid email.</li>
        </ul>
      </div>

      {exportError && (
        <div role="alert" className="text-sm rounded-lg p-3 mb-4" data-testid="stm-export-error"
          style={{ color: "var(--fb-error)", border: "1px solid var(--fb-error)" }}>
          {exportError}
        </div>
      )}

      {status === "error" && (
        <div role="alert" className="text-sm rounded-lg p-4 text-center" data-testid="stm-error"
          style={{ color: "var(--fb-error)", border: "1px solid var(--fb-error)" }}>
          <p className="mb-2">Could not load the mailing report: {error}</p>
          <button type="button" onClick={load} data-testid="stm-retry"
            className="px-3 py-1.5 rounded-lg text-xs font-semibold"
            style={{ background: "var(--fb-primary)", color: "#fff" }}>
            Retry
          </button>
        </div>
      )}

      {status === "loading" && (
        <div role="status" className="text-sm text-center py-8" style={muted} data-testid="stm-loading">
          Loading mailing report...
        </div>
      )}

      {status === "ready" && (
        <>
          <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-5" data-testid="stm-counts">
            <Count label="Total" value={counts.total} id="total" />
            <Count label="No email" value={counts.no_email} id="no-email" />
            <Count label="Senior with email" value={counts.senior_email} id="senior-email" />
          </div>

          <div role="group" aria-label="Filter by group" className="flex flex-wrap gap-1 mb-4">
            {FILTERS.map(({ key, label }) => (
              <button key={key} type="button" onClick={() => setFilter(key)} aria-pressed={group === key}
                data-testid={`stm-filter-${key}`}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                style={{
                  background: group === key ? "var(--fb-primary)" : "transparent",
                  color: group === key ? "#fff" : "var(--fb-text-muted)",
                  border: group === key ? "none" : "1px solid var(--fb-border)",
                }}>
                {label}
              </button>
            ))}
          </div>

          <div className="mb-4 rounded-lg p-3 text-sm space-y-2" style={{ border: "1px solid var(--fb-border)" }} data-testid="stm-check-panel">
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" data-testid="stm-check-open" onClick={openPanel}
                disabled={!canSpend || running || panel?.state === "loading" || sessionCandidates.length === 0}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50"
                style={{ border: "1px solid var(--fb-primary)", color: "var(--fb-primary)" }}>
                Check mailing addresses
              </button>
              <button type="button" data-testid="stm-status-checked" aria-pressed={statusFilter === "checked"}
                onClick={() => setStatus2(statusFilter === "checked" ? "all" : "checked")}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                style={{ background: statusFilter === "checked" ? "var(--fb-primary)" : "transparent", color: statusFilter === "checked" ? "#fff" : "var(--fb-text-muted)", border: statusFilter === "checked" ? "none" : "1px solid var(--fb-border)" }}>
                Checked only
              </button>
              <span className="text-xs" style={muted} data-testid="stm-check-tally">
                {tally.checked} checked, {tally.needs_review} needs review, {tally.not_checked} not checked
              </span>
            </div>
            <p className="text-xs" style={muted}>“Checked only” filters this session’s unexpired results for viewing and export, not saved verification tags.</p>
            {!canSpend && <p className="text-xs" style={muted}>Viewer accounts can read the report, but only staff or administrators can run paid address checks.</p>}
            {hasChecks && <p className="text-xs" style={muted}>
              Check results by <a href="https://cloud.google.com/maps-platform/terms" target="_blank" rel="noopener noreferrer"
                translate="no" className="underline" style={{ whiteSpace: "nowrap", fontWeight: 400, fontSize: 12, color: "#5E5E5E" }}>Google Maps</a>.
              {" "}Postal checks do not guarantee delivery or residency. Results expire after 15 minutes.
            </p>}
            {panel?.state === "loading" && <p className="text-xs" style={muted}>Loading check limits...</p>}
            {panel?.state === "error" && (
              <p role="alert" className="text-xs" style={{ color: "var(--fb-error)" }} data-testid="stm-check-config-error">
                Could not load check limits: {panel.error}{" "}
                <button type="button" className="underline" onClick={openPanel}>Retry</button>
              </p>
            )}
            {panel?.state === "ready" && cfg && (
              !cfg.google_active ? (
                <p className="text-xs" style={muted} data-testid="stm-check-inactive">Google address checks are not available right now.</p>
              ) : (
                <div className="space-y-2" data-testid="stm-check-confirm">
                  <label className="flex items-center gap-2 text-xs">
                    <input type="checkbox" data-testid="stm-recheck-verified" checked={recheckVerified}
                      onChange={(e) => { setRecheckVerified(e.target.checked); setRequested(""); }}
                      disabled={running} />
                    Recheck previously Google-verified saved addresses
                  </label>
                  <p className="text-xs" style={muted} data-testid="stm-saved-skip-count">
                    {recheckVerified ? `0 saved-address matches will be skipped; ${savedMatches} previously verified addresses are included for recheck.`
                      : `Unchecked saved addresses only: ${savedMatches} previously Google-verified saved-address matches will be skipped.`}
                    {" "}Changed addresses are not treated as verified. Saved verification tags do not expire by age.
                  </p>
                  <p className="text-xs" style={muted}>
                    {candidates.length} addresses in this view can be checked. Up to {maxAllowed} can run now ({cfg.daily_remaining} left today, 100 per run).
                    Each address is sent to Google; names and emails are not. Checks alone never change saved addresses; accepting a confirmed correction updates the record.{" "}
                    <a href="/address-check-policy" target="_blank" rel="noopener noreferrer" className="underline">Privacy and terms</a>
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="text-xs" htmlFor="stm-check-count">Addresses to check</label>
                    <input id="stm-check-count" data-testid="stm-check-count" type="number" min="1" max={maxAllowed}
                      value={requested === "" ? String(maxAllowed) : requested}
                      onChange={(e) => setRequested(e.target.value)} disabled={running}
                      className="w-20 px-2 py-1 rounded-lg border text-sm tabular-nums" style={{ borderColor: "var(--fb-border)" }} />
                    <button type="button" data-testid="stm-check-run" onClick={runBatch} disabled={running || effective < 1}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50"
                      style={{ background: "var(--fb-primary)", color: "#fff" }}>
                      Check {effective} addresses
                    </button>
                    {running && (
                      <button type="button" data-testid="stm-check-cancel" onClick={() => { cancelRef.current = true; }}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold" style={{ border: "1px solid var(--fb-border)" }}>
                        Stop after this batch
                      </button>
                    )}
                  </div>
                </div>
              )
            )}
            {batch && (
              <p className="text-xs" role="status" data-testid="stm-check-progress" style={batch.error ? { color: "var(--fb-error)" } : muted}>
                {batch.running ? "Checking" : batch.cancelled ? "Stopped" : batch.error ? "Partly finished" : "Finished"}: {batch.done} of {batch.total} done.
                {batch.skipped > 0 ? ` ${batch.skipped} already verified on the server and skipped without a Google call.` : ""}
                {batch.error ? ` ${batch.failed} not checked: ${batch.error}. Run again to retry them.` : ""}
              </p>
            )}
          </div>

          {rows.length === 0 ? (
            <div className="text-sm text-center py-8" style={muted} data-testid="stm-empty">
              No households match this group for the period.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left" data-testid="stm-table">
                  <caption className="sr-only">Households to include in the Sharing Tree mailing</caption>
                  <thead>
                    <tr className="text-xs uppercase tracking-wider" style={{ color: "#8B918B" }}>
                      <th scope="col" className="py-2 pr-3">Name</th>
                      <th scope="col" className="py-2 pr-3">Mailing address</th>
                      <th scope="col" className="py-2 pr-3">Email</th>
                       <th scope="col" className="py-2 pr-3">Primary age</th>
                      <th scope="col" className="py-2 pr-3">Last visit</th>
                      <th scope="col" className="py-2 pr-3">Reason</th>
                      <th scope="col" className="py-2">Address check</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((r) => (
                      <tr key={r.household_id} className="align-top" style={{ borderTop: "1px solid var(--fb-border)" }} data-testid={`stm-row-${r.household_id}`}>
                        <td className="py-2 pr-3 font-medium">{r.first_name} {r.last_name}</td>
                        <td className="py-2 pr-3 min-w-[12rem]">{r.full_address}{savedVerified(r) && <span data-testid={`stm-google-verified-${r.household_id}`} className="block text-[11px] font-semibold" style={{ color: "#1E40AF" }}>Google verified</span>}</td>
                        <td className="py-2 pr-3 break-all">{r.email || "None"}</td>
                         <td className="py-2 pr-3 tabular-nums">{r.primary_age ?? "Unknown"}</td>
                        <td className="py-2 pr-3 whitespace-nowrap">{formatVisitDate(r.last_visit)}</td>
                        <td className="py-2 pr-3">{r.reason}</td>
                        <td className="py-2 text-xs" data-testid={`stm-check-${r.household_id}`}>
                          {validCheck(r) ? ({ checked: "Checked", needs_review: "Needs review", not_checked: "Not checked" }[validCheck(r).status] || "Not checked") : "Not checked"}
                          {validCheck(r)?.message && <p className="mt-1 min-w-[12rem]" style={muted}>{validCheck(r).message}</p>}
                          {validCheck(r)?.status === "needs_review" && validCheck(r).address && (() => {
                            const c = validCheck(r);
                            const a = c.address;
                            const confirmed = !!(c.suggestion_confirmed && c.verification_token);
                            const st = rowState[r.household_id] || {};
                            return (
                              <div className="mt-1 min-w-[12rem] space-y-1" data-testid={`stm-suggestion-${r.household_id}`}>
                                <p>Suggested: {[[a.street, a.apt].filter(Boolean).join(" "), a.city, [a.state, a.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</p>
                                {c.kept ? <p style={muted}>Keeping the original address.</p> : (
                                  <div className="flex flex-wrap gap-1.5">
                                    {confirmed && (
                                      <button type="button" data-testid={`stm-accept-${r.household_id}`} disabled={!!st.saving || !canSpend}
                                        onClick={() => acceptSuggestion(r)} className="px-2 py-1 rounded-lg text-xs font-semibold disabled:opacity-50"
                                        style={{ background: "var(--fb-primary)", color: "#fff" }}>
                                        {st.saving ? "Saving..." : "Use suggested address"}
                                      </button>
                                    )}
                                    <button type="button" data-testid={`stm-keep-${r.household_id}`} disabled={!!st.saving}
                                      onClick={() => keepOriginal(r.household_id)} className="px-2 py-1 rounded-lg text-xs font-semibold disabled:opacity-50"
                                      style={{ border: "1px solid var(--fb-border)" }}>Keep original</button>
                                  </div>
                                )}
                                {!confirmed && !c.kept && <p style={muted}>This suggestion is not confirmed and cannot be applied.</p>}
                                {st.error && <p role="alert" data-testid={`stm-accept-error-${r.household_id}`} style={{ color: "var(--fb-error)" }}>{st.error}</p>}
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between gap-3 mt-3 text-xs" style={muted}>
                <span data-testid="stm-page-info">
                  Showing {safePage * PAGE_SIZE + 1}-{safePage * PAGE_SIZE + visible.length} of {rows.length}
                </span>
                {pageCount > 1 && (
                  <span className="flex gap-2">
                    <button type="button" data-testid="stm-prev" disabled={safePage === 0}
                      onClick={() => setPage(safePage - 1)}
                      className="px-3 py-1 rounded-lg disabled:opacity-50" style={{ border: "1px solid var(--fb-border)" }}>Previous</button>
                    <button type="button" data-testid="stm-next" disabled={safePage >= pageCount - 1}
                      onClick={() => setPage(safePage + 1)}
                      className="px-3 py-1 rounded-lg disabled:opacity-50" style={{ border: "1px solid var(--fb-border)" }}>Next</button>
                  </span>
                )}
              </div>
            </>
          )}
        </>
      )}

      <div className="text-xs mt-5 space-y-1" style={muted} data-testid="stm-notes">
        <p>A complete address is not the same as a deliverable postal address.</p>
        <p>A valid email format does not guarantee the message is delivered.</p>
        <p>These outreach criteria do not prove a household cannot sign up online.</p>
         <p>When importing the CSV into a spreadsheet, set the ZIP column to text to preserve leading zeros.</p>
      </div>
    </section>
  );
}

function Count({ label, value, id }) {
  return (
    <div className="rounded-xl p-3 sm:p-4 text-center" style={{ background: "var(--fb-primary)08", border: "1px solid var(--fb-primary)25" }} data-testid={`stm-count-${id}`}>
      <div className="text-2xl sm:text-3xl font-bold tabular-nums" style={{ fontFamily: "Outfit", color: "var(--fb-primary)" }}>
        {(value ?? 0).toLocaleString()}
      </div>
      <div className="text-[11px] uppercase tracking-wider font-semibold mt-1" style={{ color: "#8B918B" }}>{label}</div>
    </div>
  );
}
